import { FunctionComponent } from 'react';
import { Tooltip } from '@mui/material';
import { Battery20, Battery60, BatteryFull, BatteryUnknown } from '@mui/icons-material';
import { SvgIconOwnProps } from '@mui/material/SvgIcon';
import { ICellRendererParams } from 'ag-grid-community';
import { useTranslation } from '@/lib';
import { useDate } from '@/hooks/useDate';
import { BatteryStatus } from '@/modules/trackers/api/trackers/tracker.contracts';

export interface IBatteryStatusCellRendererParams {
  batteryStatus?: BatteryStatus | null;
  /** When the tracker last reported the level, appended to the tooltip when it is known. */
  updatedAt?: Date | null;
}

interface IBatteryStatusCellInternalRendererParams extends IBatteryStatusCellRendererParams, ICellRendererParams {}

/**
 * Renders the qualitative battery level as a battery icon: full and green for high, half and orange
 * for medium, nearly empty and red for low, and a grey battery with a question mark when the tracker
 * has never reported a level. The level itself is in the tooltip.
 */
export const BatteryStatusCellRenderer: FunctionComponent<IBatteryStatusCellInternalRendererParams> = (props) => {
  const { t } = useTranslation();
  const { formatDateTimestampShort } = useDate();

  const batteryStatus = props.batteryStatus ?? null;
  const iconProps: SvgIconOwnProps = { fontSize: 'small' };

  let icon;
  let label: string;

  switch (batteryStatus) {
    case BatteryStatus.HIGH:
      icon = <BatteryFull {...iconProps} color="success" />;
      label = t('battery_status_high');
      break;
    case BatteryStatus.MEDIUM:
      icon = <Battery60 {...iconProps} color="warning" />;
      label = t('battery_status_medium');
      break;
    case BatteryStatus.LOW:
      icon = <Battery20 {...iconProps} color="error" />;
      label = t('battery_status_low');
      break;
    default:
      icon = <BatteryUnknown {...iconProps} color="disabled" />;
      label = t('battery_status_unknown');
      break;
  }

  const tooltip =
    batteryStatus && props.updatedAt
      ? t('battery_status_updated_at', { status: label, date: formatDateTimestampShort(props.updatedAt) })
      : label;

  return (
    <Tooltip title={tooltip} placement="left">
      <div className="flex h-full items-center">{icon}</div>
    </Tooltip>
  );
};
